import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, FunnelIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Filters from '../Table/Filters';
import SharedButton from '../../global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import { getFilterLabel } from '../../modules/search/searchGrid/helpers/getFiltersLabels';
import SavedFiltersModal from './SavedFiltersModal';
import { userAppContext } from '../../context/Context';
import { UserContext } from '../../types';
import Alert from '../../global/Alert/Alert';
import { usePrivacyPolicy } from '../../hooks/usePrivacyPolicy';
import PrivacyPolicyModal from '../Common/PrivacyPolicyModal';
import '../../modules/search/searchGrid/components/table/SummaryMatchBar.css';
import '../../modules/search/searchGrid/SearchResults.css';
import BaseTooltip from '@/global/Tooltip/BaseTooltip';
import {
  ActiveFilterChip,
  buildActiveFilterChips,
  getActiveFiltersSummary,
  groupChipsByCategory,
} from './activeFiltersHelpers';
import './ActiveFiltersBar.css';

export const ACTIVE_FILTERS_BAR_SLOT_ID = 'active-filters-bar-slot';

type ActiveFiltersBarProps = {
  filters: any[];
  onRemoveFilter: (filter: any) => void;
  onRemoveFilterValue?: (filterName: string, valueToRemove: string) => void;
  onClearAll: () => void;
  isEvidence?: boolean;
  showSummaryMatchBar?: boolean;
  onOpenSummaryMatch?: () => void;
  isSummaryMatchLoading?: boolean;
  summaryMatchActive?: boolean;
  summaryMatchStats?: any;
  summaryMatchError?: string | null;
  onClearSummaryMatch?: () => void;
  portalTargetId?: string;
};

const ActiveFiltersBar = ({
  filters,
  onRemoveFilter,
  onRemoveFilterValue,
  onClearAll,
  isEvidence = false,
  showSummaryMatchBar = false,
  onOpenSummaryMatch,
  isSummaryMatchLoading = false,
  summaryMatchActive = false,
  summaryMatchStats = null,
  summaryMatchError = null,
  onClearSummaryMatch,
  portalTargetId,
}: ActiveFiltersBarProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState(false);
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = userAppContext() as unknown as UserContext;
  const { markAsAccepted } = usePrivacyPolicy();

  const showSummaryTableButton = process.env.REACT_APP_SHOW_SUMMARY_TABLE_BUTTON === 'true';

  const activeFilters = filters.filter(
    f =>
      f.value !== undefined &&
      f.value !== null &&
      f.value !== '' &&
      !(Array.isArray(f.value) && f.value.length === 0),
  );

  const activeFiltersWithLabels = activeFilters.map(activeFilter => {
    const matchingFilter = Filters.find(filter => filter.name === activeFilter.name);
    return {
      ...activeFilter,
      label: matchingFilter ? matchingFilter.label : 'Unknown',
    };
  });

  const allChips = buildActiveFilterChips(activeFiltersWithLabels, getFilterLabel);
  const { chipCount, categoryCount } = getActiveFiltersSummary(allChips);

  const normalizedSearchTerm = filterSearchTerm.trim().toLowerCase();
  const visibleChips = allChips.filter(
    chip => !normalizedSearchTerm || chip.label.toLowerCase().includes(normalizedSearchTerm),
  );
  const groupedChips = groupChipsByCategory(visibleChips);

  useEffect(() => {
    if (!isPanelOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsPanelOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsPanelOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isPanelOpen]);

  useEffect(() => {
    if (activeFilters.length === 0) {
      setIsPanelOpen(false);
      setFilterSearchTerm('');
    }
  }, [activeFilters.length]);

  useLayoutEffect(() => {
    if (!portalTargetId) {
      setPortalTarget(null);
      return;
    }
    setPortalTarget(document.getElementById(portalTargetId));
  }, [portalTargetId]);

  const handleApplyFilter = savedFilters => {
    onClearAll();

    if ((window as any).gridRef?.current) {
      (window as any).gridRef.current.setFilterValue(savedFilters);
    }
  };

  const handleSaveSuccess = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessAlert(true);
    setTimeout(() => setShowSuccessAlert(false), 3000);
  };

  const handlePrivacyPolicyAccept = () => {
    markAsAccepted();
    setShowPrivacyPolicyModal(false);
    setIsModalOpen(true);
  };

  const handlePrivacyPolicyClose = () => {
    setShowPrivacyPolicyModal(false);
  };

  const handleRemoveChip = (chip: ActiveFilterChip) => {
    if (chip.itemValue != null && onRemoveFilterValue) {
      onRemoveFilterValue(chip.filterName, chip.itemValue);
      return;
    }
    onRemoveFilter(chip.filter);
  };

  if (activeFilters.length === 0 && !summaryMatchActive) return null;

  const statsLine =
    summaryMatchStats &&
    `Summary filtered to ${summaryMatchStats.matchFilterCount} ${summaryMatchStats.matchFilterLabel} from filtered Evidence (${summaryMatchStats.uniqueAssociationCount} association${summaryMatchStats.uniqueAssociationCount !== 1 ? 's' : ''}, ${summaryMatchStats.evidenceRowCount} Evidence row${summaryMatchStats.evidenceRowCount !== 1 ? 's' : ''}). You can apply additional Summary filters below.`;

  const categoryLabel = categoryCount === 1 ? 'type' : 'types';
  const filterLabel = chipCount === 1 ? 'filter' : 'filters';

  const bar =
    activeFilters.length > 0 ? (
      <div className="active-filters-bar mt-2 mb-3 mr-8" ref={panelRef}>
        <div className="active-filters-summary-row">
          <SharedButton
            id="active-filters-summary-trigger"
            aria-expanded={isPanelOpen}
            aria-controls="active-filters-panel"
            variant="ghost"
            size="xs"
            onClick={() => setIsPanelOpen(open => !open)}
            className="active-filters-summary-trigger mb-0"
            iconLeft={<FunnelIcon className="h-4 w-4 shrink-0 text-blue-700" />}
            iconRight={
              <ChevronDownIcon
                className="h-4 w-4 shrink-0 text-blue-700 transition-transform duration-200"
                style={{ transform: isPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            }
          >
            <span className="font-medium text-slate-800">
              {chipCount} {filterLabel}
              <span className="mx-1.5 text-slate-400">·</span>
              {categoryCount} {categoryLabel}
            </span>
          </SharedButton>

          {showSummaryMatchBar && onOpenSummaryMatch && showSummaryTableButton && (
            <BaseTooltip content="View which Summary associations match these results." position="top">
              <SharedButton
                variant="secondaryOutline"
                size="xs"
                onClick={onOpenSummaryMatch}
                disabled={isSummaryMatchLoading}
                style={{ minWidth: 'max-content' }}
                className="mb-0 mt-0"
              >
                {isSummaryMatchLoading ? 'Building match…' : 'View Filtered Summary'}
              </SharedButton>
            </BaseTooltip>
          )}
        </div>

        {isPanelOpen && (
          <div
            id="active-filters-panel"
            role="region"
            aria-labelledby="active-filters-summary-trigger"
            className="active-filters-panel"
          >
            <div className="active-filters-panel-header">
              <div className="relative min-w-0 flex-1">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 h-4 w-4 -translate-y-1/2 text-slate-400 mt-2.5" />
                <input
                  type="text"
                  value={filterSearchTerm}
                  onChange={e => setFilterSearchTerm(e.target.value)}
                  placeholder="Search applied filters..."
                  className="filter-input active-filters-search-input"
                />
              </div>
              <SharedButton
                variant="secondaryText"
                size="xs"
                onClick={onClearAll}
                className="mb-0 shrink-0 mt-0"
              >
                Clear all
              </SharedButton>
            </div>

            <div className="active-filters-panel-body">
              {groupedChips.length === 0 ? (
                <p className="mb-0 px-1 py-2 text-sm text-slate-500">No filters match your search.</p>
              ) : (
                groupedChips.map(group => (
                  <div key={group.category} className="active-filters-group">
                    <p className="active-filters-group-title">{group.category}</p>
                    <div className="active-filters-chip-list">
                      {group.chips.map(chip => (
                        <SharedBadge
                          key={chip.key}
                          text={
                            <span className="search-items-tag-content flex min-w-0 max-w-[18rem] items-center gap-1.5">
                              <span
                                className="search-items-tag-label min-w-0 flex-1 truncate"
                                title={chip.label}
                              >
                                {chip.label}
                              </span>
                              <span aria-hidden="true" className="search-items-tag-divider" />
                              <button
                                type="button"
                                onClick={() => handleRemoveChip(chip)}
                                className="search-items-tag-close shrink-0 border-0 bg-transparent pl-0.5"
                                aria-label={`Remove ${chip.label}`}
                              >
                                <XMarkIcon className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          }
                          variant="primaryOutline"
                          className="search-items-tag !mb-0 flex h-auto min-h-7 max-w-full items-center justify-start overflow-hidden text-left"
                          style={{ marginBottom: 0 }}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    ) : null;

  const placedBar = portalTarget && bar ? createPortal(bar, portalTarget) : bar;

  return (
    <>
      {showSuccessAlert && <Alert message={successMessage} type="success" />}

      {summaryMatchActive && !isEvidence && (statsLine || summaryMatchError) && (
        <div className={`summary-match-bar mr-8${summaryMatchError ? ' summary-match-bar--error' : ''}`}>
          <span className="summary-match-bar-text">{summaryMatchError || statsLine}</span>
          {onClearSummaryMatch && (
            <SharedButton variant="secondaryText" size="xs" onClick={onClearSummaryMatch} className="mb-0">
              Clear filters
            </SharedButton>
          )}
        </div>
      )}

      {placedBar}

      {isAuthenticated && (
        <>
          <SavedFiltersModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onApplyFilter={handleApplyFilter}
            currentFilters={filters}
            isEvidence={isEvidence}
            onSaveSuccess={handleSaveSuccess}
          />

          <PrivacyPolicyModal
            isOpen={showPrivacyPolicyModal}
            onClose={handlePrivacyPolicyClose}
            onAccept={handlePrivacyPolicyAccept}
          />
        </>
      )}
    </>
  );
};

export default ActiveFiltersBar;
